import type { Tile, TileColor, BoardState } from './types';
import { BOARD_WIDTH, BOARD_HEIGHT, NUM_COLORS } from './types';
import { SeededRNG } from './rng';

/**
 * Creates a new board with randomly colored tiles
 */
export function createBoard(rng: SeededRNG): BoardState {
  const grid: Tile[][] = [];

  for (let y = 0; y < BOARD_HEIGHT; y++) {
    const row: Tile[] = [];
    for (let x = 0; x < BOARD_WIDTH; x++) {
      row.push({
        color: rng.randomInt(NUM_COLORS) as TileColor,
        alive: true,
      });
    }
    grid.push(row);
  }

  return {
    grid,
    score: 0,
  };
}

/**
 * Deep clone a board state
 */
export function cloneBoard(board: BoardState): BoardState {
  return {
    grid: board.grid.map((row) => row.map((tile) => ({ ...tile }))),
    score: board.score,
  };
}

/**
 * Find all tiles in a group starting from (x, y) using flood-fill
 * Returns array of coordinates in the group
 */
export function findGroup(
  grid: Tile[][],
  startX: number,
  startY: number
): Array<{ x: number; y: number }> {
  const tile = grid[startY]?.[startX];
  if (!tile || !tile.alive) return [];

  const targetColor = tile.color;
  const group: Array<{ x: number; y: number }> = [];
  const visited = new Set<string>();
  const queue: Array<{ x: number; y: number }> = [{ x: startX, y: startY }];

  while (queue.length > 0) {
    const pos = queue.shift()!;
    const key = `${pos.x},${pos.y}`;

    if (visited.has(key)) continue;
    visited.add(key);

    const currentTile = grid[pos.y]?.[pos.x];
    if (
      !currentTile ||
      !currentTile.alive ||
      currentTile.color !== targetColor
    ) {
      continue;
    }

    group.push(pos);

    // Check all 4 adjacent tiles
    queue.push({ x: pos.x - 1, y: pos.y }); // left
    queue.push({ x: pos.x + 1, y: pos.y }); // right
    queue.push({ x: pos.x, y: pos.y - 1 }); // up
    queue.push({ x: pos.x, y: pos.y + 1 }); // down
  }

  return group;
}

/**
 * Remove a group of tiles from the board
 * Returns true if group was removed (size >= 2)
 */
export function removeGroup(board: BoardState, x: number, y: number): boolean {
  const group = findGroup(board.grid, x, y);

  // Only remove groups of 2 or more
  if (group.length < 2) return false;

  // Mark tiles as not alive
  for (const pos of group) {
    board.grid[pos.y][pos.x].alive = false;
  }

  // Calculate and add score
  const groupSize = group.length;
  const points = groupSize * (groupSize - 1);
  board.score += points;

  return true;
}

/**
 * Apply gravity - make tiles fall down to fill empty spaces
 */
export function applyGravity(grid: Tile[][]): void {
  const height = grid.length;
  const width = grid[0]?.length || 0;

  for (let x = 0; x < width; x++) {
    // For each column, collect all alive tiles from bottom to top
    const aliveTiles: Tile[] = [];
    for (let y = height - 1; y >= 0; y--) {
      if (grid[y][x].alive) {
        aliveTiles.push(grid[y][x]);
      }
    }

    // Place alive tiles at the bottom
    for (let y = height - 1; y >= 0; y--) {
      const tileIndex = height - 1 - y;
      if (tileIndex < aliveTiles.length) {
        grid[y][x] = aliveTiles[tileIndex];
      } else {
        // Fill top with dead tiles
        grid[y][x] = { ...grid[y][x], alive: false };
      }
    }
  }
}

/**
 * Compact columns left - remove empty columns and shift remaining left
 */
export function compactLeft(grid: Tile[][]): void {
  const height = grid.length;
  const width = grid[0]?.length || 0;
  const nonEmptyColumns: Tile[][] = [];

  // Collect all non-empty columns
  for (let x = 0; x < width; x++) {
    let hasAliveTile = false;
    for (let y = 0; y < height; y++) {
      if (grid[y][x].alive) {
        hasAliveTile = true;
        break;
      }
    }

    if (hasAliveTile) {
      // Extract this column
      const column: Tile[] = [];
      for (let y = 0; y < height; y++) {
        column.push(grid[y][x]);
      }
      nonEmptyColumns.push(column);
    }
  }

  // Place non-empty columns at the left, fill rest with dead tiles
  for (let x = 0; x < width; x++) {
    if (x < nonEmptyColumns.length) {
      for (let y = 0; y < height; y++) {
        grid[y][x] = nonEmptyColumns[x][y];
      }
    } else {
      for (let y = 0; y < height; y++) {
        grid[y][x] = { color: 0, alive: false };
      }
    }
  }
}

/**
 * Check if there are any valid moves left (any adjacent same-color tiles)
 */
export function hasMoves(grid: Tile[][]): boolean {
  const height = grid.length;
  const width = grid[0]?.length || 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const tile = grid[y]?.[x];
      if (!tile || !tile.alive) continue;

      // Check right neighbor
      if (
        x + 1 < width &&
        grid[y][x + 1]?.alive &&
        grid[y][x + 1].color === tile.color
      ) {
        return true;
      }

      // Check down neighbor
      if (
        y + 1 < height &&
        grid[y + 1]?.[x]?.alive &&
        grid[y + 1][x].color === tile.color
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Check if the grid is completely empty (no alive tiles)
 */
export function isGridEmpty(grid: Tile[][]): boolean {
  const height = grid.length;
  const width = grid[0]?.length || 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (grid[y]?.[x]?.alive) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Calculate score for a group of given size
 */
export function calculateGroupScore(groupSize: number): number {
  return groupSize * (groupSize - 1);
}
